import { FiUsers, FiTruck, FiAlertTriangle, FiAlertCircle } from "react-icons/fi";

interface BigNumbersSectionProps {
  totalActiveClients: number;
  deliveryOnlyClients: number;
  emergencyClients: number;
  criticalClients: number;
  dataLoading: boolean;
}

export function BigNumbersSection({
  totalActiveClients,
  deliveryOnlyClients,
  emergencyClients,
  criticalClients,
  dataLoading,
}: BigNumbersSectionProps) {
  return (
    <div
      className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 transition-opacity duration-300 ${
        dataLoading ? "opacity-50" : "opacity-100"
      }`}
    >
      {/* Card Clientes no Mapa */}
      <div className="group relative bg-white rounded-2xl p-3 sm:p-5 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden">
        <div className="absolute top-0 right-0 w-12 sm:w-20 h-12 sm:h-20 bg-gradient-to-br from-[#0085BD]/10 to-[#006a96]/5 rounded-bl-full" />
        <div className="relative flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-xl bg-gradient-to-br from-[#0085BD] to-[#006a96] flex items-center justify-center shadow-lg shadow-[#0085BD]/30 group-hover:scale-110 transition-transform duration-300">
            <FiUsers size={18} className="text-white sm:hidden" />
            <FiUsers size={22} className="text-white hidden sm:block" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#0085BD] to-[#006a96] bg-clip-text text-transparent">
              {totalActiveClients}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 font-medium">
              Clientes no Mapa
            </p>
          </div>
        </div>
      </div>

      {/* Card Apenas Entregas */}
      <div className="group relative bg-white rounded-2xl p-3 sm:p-5 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden">
        <div className="absolute top-0 right-0 w-12 sm:w-20 h-12 sm:h-20 bg-gradient-to-br from-gray-500/10 to-slate-500/5 rounded-bl-full" />
        <div className="relative flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-xl bg-gradient-to-br from-gray-500 to-slate-500 flex items-center justify-center shadow-lg shadow-gray-500/30 group-hover:scale-110 transition-transform duration-300">
            <FiTruck size={18} className="text-white sm:hidden" />
            <FiTruck size={22} className="text-white hidden sm:block" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-gray-600 to-slate-600 bg-clip-text text-transparent">
              {deliveryOnlyClients}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 font-medium">
              Apenas Entregas
            </p>
          </div>
        </div>
      </div>

      {/* Card Emergências */}
      <div
        className={`group relative bg-white rounded-2xl p-3 sm:p-5 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden ${
          emergencyClients > 0 ? "ring-2 ring-red-500/30 animate-pulse" : ""
        }`}
      >
        {emergencyClients > 0 && (
          <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-rose-500/10 pointer-events-none" />
        )}
        <div className="absolute top-0 right-0 w-12 sm:w-20 h-12 sm:h-20 bg-gradient-to-br from-red-500/10 to-rose-500/5 rounded-bl-full" />
        <div className="relative flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-xl bg-gradient-to-br from-red-500 to-rose-500 flex items-center justify-center shadow-lg shadow-red-500/30 group-hover:scale-110 transition-transform duration-300">
            <FiAlertTriangle size={18} className="text-white sm:hidden" />
            <FiAlertTriangle size={22} className="text-white hidden sm:block" />
          </div>
          <div className="flex-1 min-w-0">
            <p
              className={`text-xl sm:text-2xl font-bold bg-gradient-to-r from-red-600 to-rose-600 bg-clip-text text-transparent ${
                emergencyClients > 0 ? "animate-pulse" : ""
              }`}
            >
              {emergencyClients}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 font-medium">
              Emergências
            </p>
          </div>
        </div>
      </div>

      {/* Card Críticos */}
      <div className="group relative bg-white rounded-2xl p-3 sm:p-5 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden">
        <div className="absolute top-0 right-0 w-12 sm:w-20 h-12 sm:h-20 bg-gradient-to-br from-orange-500/10 to-amber-500/5 rounded-bl-full" />
        <div className="relative flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-500/30 group-hover:scale-110 transition-transform duration-300">
            <FiAlertCircle size={18} className="text-white sm:hidden" />
            <FiAlertCircle size={22} className="text-white hidden sm:block" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
              {criticalClients}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 font-medium">
              Críticos (+45 dias)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
